import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link } from "react-router-dom";

const Features = () => {
  const featureData = {
    trial: [
      {
        feature: "Document Storage",
        description: "Store up to 100 documents securely in the cloud with enterprise-grade encryption and access controls."
      },
      {
        feature: "AI Search Queries",
        description: "Use natural language to search through your documents with 50 AI-powered queries per month."
      },
      {
        feature: "Basic Export",
        description: "Export search results and relevant document batches in PDF and CSV formats."
      },
      {
        feature: "Email Support",
        description: "Get help when you need it with email support during business hours."
      },
      {
        feature: "Single User Access",
        description: "Perfect for individual practitioners or small teams getting started with AI document search."
      }
    ],
    pro: [
      {
        feature: "Document Storage",
        description: "Store up to 10,000 documents with advanced organization, tagging, and categorization features."
      },
      {
        feature: "Unlimited AI Search",
        description: "Unlimited natural language queries with advanced filtering, sorting, and relevance scoring."
      },
      {
        feature: "Advanced Export",
        description: "Export with custom formatting, metadata inclusion, and batch processing capabilities."
      },
      {
        feature: "Multi-User Collaboration",
        description: "Team workspace for up to 10 users with role-based permissions and shared document libraries."
      },
      {
        feature: "Phone & Email Support",
        description: "Priority support via phone and email with faster response times and dedicated support agents."
      },
      {
        feature: "Custom Integrations",
        description: "Connect with popular legal software including practice management systems and document repositories."
      },
      {
        feature: "Advanced Analytics",
        description: "Detailed insights into search patterns, document usage, and team productivity metrics."
      },
      {
        feature: "API Access",
        description: "RESTful API access for custom integrations and workflow automation."
      },
      {
        feature: "Bulk Operations",
        description: "Process multiple documents simultaneously with batch upload, search, and export capabilities."
      },
      {
        feature: "Custom Templates",
        description: "Create and save custom search templates and export formats for recurring case types."
      }
    ],
    enterprise: [
      {
        feature: "Unlimited Storage",
        description: "Unlimited document storage with advanced data management, archiving, and compliance features."
      },
      {
        feature: "Advanced AI Models",
        description: "Access to the latest AI models with custom training on your firm's specific legal domain and terminology."
      },
      {
        feature: "Enterprise SSO",
        description: "Single sign-on integration with SAML 2.0, Active Directory, and other enterprise identity providers."
      },
      {
        feature: "Dedicated Account Manager",
        description: "Personal account manager for strategic planning, training, and ongoing optimization of your AI workflows."
      },
      {
        feature: "Custom Training",
        description: "Personalized training sessions for your team and custom AI model training on your specific case types."
      },
      {
        feature: "SLA Guarantee",
        description: "99.9% uptime guarantee with dedicated infrastructure and priority technical support."
      },
      {
        feature: "Advanced Security",
        description: "Enhanced security features including audit logs, data residency options, and custom compliance reporting."
      },
      {
        feature: "White-Label Options",
        description: "Custom branding and white-label deployment options for client-facing applications."
      },
      {
        feature: "Custom Workflows",
        description: "Build custom automated workflows with conditional logic, approval processes, and integration points."
      },
      {
        feature: "Data Analytics Suite",
        description: "Advanced business intelligence tools with custom dashboards, reporting, and predictive analytics."
      },
      {
        feature: "Priority Processing",
        description: "Dedicated computing resources ensuring faster processing times for large document sets."
      },
      {
        feature: "Compliance Suite",
        description: "Built-in compliance tools for various legal standards including GDPR, CCPA, and industry-specific regulations."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-primary to-primary-glow rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">E</span>
            </div>
            <span className="text-xl font-bold">ExhibitAI</span>
          </Link>
          <div className="flex items-center space-x-4">
            <Link to="/login">
              <Button variant="ghost">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button>Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-16 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
          Features Overview
        </h1>
        <p className="text-xl text-muted-foreground mb-12 max-w-3xl mx-auto">
          Explore the comprehensive features available in each ExhibitAI plan. 
          From basic document search to enterprise-grade AI workflows.
        </p>
      </section>

      {/* Features Tabs */}
      <section className="container mx-auto px-6 pb-20">
        <Card className="max-w-6xl mx-auto">
          <CardContent className="p-8">
            <Tabs defaultValue="trial" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-8">
                <TabsTrigger value="trial">Trial Features</TabsTrigger>
                <TabsTrigger value="pro">Pro Features</TabsTrigger>
                <TabsTrigger value="enterprise">Enterprise Features</TabsTrigger>
              </TabsList>

              <TabsContent value="trial">
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold mb-2">Trial Plan Features</h2>
                    <p className="text-muted-foreground">Perfect for getting started with AI-powered document search</p>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-1/3">Feature</TableHead>
                        <TableHead>Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {featureData.trial.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{item.feature}</TableCell>
                          <TableCell className="text-muted-foreground">{item.description}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              <TabsContent value="pro">
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold mb-2">Pro Plan Features</h2>
                    <p className="text-muted-foreground">Advanced features for growing law firms and legal teams</p>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-1/3">Feature</TableHead>
                        <TableHead>Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {featureData.pro.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{item.feature}</TableCell>
                          <TableCell className="text-muted-foreground">{item.description}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              <TabsContent value="enterprise">
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold mb-2">Enterprise Plan Features</h2>
                    <p className="text-muted-foreground">Comprehensive solutions for large firms with complex requirements</p>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-1/3">Feature</TableHead>
                        <TableHead>Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {featureData.enterprise.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{item.feature}</TableCell>
                          <TableCell className="text-muted-foreground">{item.description}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </Tabs>

            <div className="mt-12 text-center">
              <p className="text-muted-foreground mb-6">Ready to get started?</p>
              <div className="flex justify-center space-x-4">
                <Link to="/pricing">
                  <Button>View Pricing</Button>
                </Link>
                <Link to="/register">
                  <Button variant="outline">Start Free Trial</Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
};

export default Features;
